import React, {useEffect} from 'react';
import {useQuery} from 'react-query';
import {AxiosResponse} from 'axios';
import {DY, createGAEvent, pushDataLayer} from '@optimaxdev/analytics/mobile';
import {isEmpty, keys} from 'ramda';

import {
    PREDICTION,
    CHOOSE_ENDPOINT,
    DY_DATA_QUERY_KEY,
    ANALYTICS,
    DEFAULT_ANALYTICS_LABEL,
    DY_SELECTOR,
} from 'features/experiments/constants';
import {
    DyDataContextType,
    RawDyVariationsType,
    DyJsonType,
} from 'features/experiments/dyDataContext/types';
import {DyCustomJsonResponseType} from 'features/experiments/types';
import {sortDyDataBySelector} from 'features/experiments/dyDataContext/libs';
import {instanceDyChoose} from 'libs/api';

export const initialData: DyDataContextType = {
    'M - HP - Recommendations': {value: ''},
    'M - CP - Bottom Recommendations': {value: ''},
};

/**
 * Get sorted data from DY for DyDataContext
 * @param {RawDyVariationsType<DyJsonType> | undefined} data with variations from response
 * @return {SortedDyVariations} sorting data
 */
const getSortedDyData = (data?: RawDyVariationsType<DyJsonType>) =>
    keys(initialData).reduce(
        (acc, key) => ({
            ...acc,
            [key]: (data && data[key] && data[key][0]?.payload?.data?.custom) ||
                (data && data[key] && data[key][0]?.payload?.data) || {
                    value: '',
                },
        }),
        {
            'M - HP - Recommendations': {value: ''},
            'M - CP - Bottom Recommendations': {value: ''},
        },
    );

/**
 * Send analytics for a single variation
 * @param {DyJsonType} data - variation data
 * @param {string} selector - variation key
 */
export const sendAnalyticsForVariation = (data: DyJsonType, selector: string) => {
    const {analytics = {}, value} = data;
    const {label, action} = analytics;
    const customizedEvent = ANALYTICS[selector];

    if (customizedEvent) {
        pushDataLayer(
            createGAEvent(
                DY,
                {action: action || customizedEvent[value], label: label || value},
                false,
            ),
        );
    } else {
        pushDataLayer(
            createGAEvent(DY, {action: `M - ${action || selector}`, label: label || value}, false),
        );
    }
};

/**
 * Send analytics based on the prediction value received from the DY API
 * @param {VariationsType<DyJsonType>} data - DY API data
 */
export const sendAnalytics = (data: RawDyVariationsType<DyJsonType>) => {
    Object.keys(data).forEach(selector => {
        const dyExperiment = data[selector][0];

        // The user was not in the targeting settings for this selector
        if (!dyExperiment?.payload) return;

        // The user has entered the experiment, the presence of the data field determines the variation or control group
        if (dyExperiment.payload.data) {
            sendAnalyticsForVariation(dyExperiment.payload.data, selector);
        } else {
            pushDataLayer(
                createGAEvent(
                    DY,
                    {action: `M - ${selector}`, label: DEFAULT_ANALYTICS_LABEL},
                    false,
                ),
            );
        }
    });
};

/**
 * Get a experiment data from DY
 * @return {Promise<RawDyVariationsType<DyJsonType>>} experiment value
 */
export const getDyData = async (): Promise<RawDyVariationsType<DyJsonType>> => {
    const {data}: AxiosResponse<DyCustomJsonResponseType<DyJsonType>> = await instanceDyChoose({
        method: 'POST',
        url: CHOOSE_ENDPOINT,
        data: {
            selector: {
                names: [DY_SELECTOR.HP_RECOMMENDATION, DY_SELECTOR.CATEGORY_BOTTOM_RECOMMENDATION],
            },
            context: {
                page: {
                    type: PREDICTION.type,
                },
            },
        },
    });

    return sortDyDataBySelector(data);
};

export const DyDataStateContext = React.createContext(initialData);

/**
 * Custom hook for getting the state of the prediction
 * @return {Object} state of the prediction
 */
export const useDyDataContext = () => React.useContext(DyDataStateContext);

/**
 * Provider to pass the prediction variant
 * @return {React.FC} rendered component
 */
export const DyDataProvider = ({children}) => {
    const {data} = useQuery<RawDyVariationsType<DyJsonType>, Error>(DY_DATA_QUERY_KEY, getDyData, {
        refetchInterval: false,
        refetchIntervalInBackground: false,
        refetchOnWindowFocus: false,
    });

    useEffect(() => {
        if (data && !isEmpty(data)) {
            sendAnalytics(data);
        }
    }, [data]);

    return (
        <DyDataStateContext.Provider value={getSortedDyData(data)}>
            {children}
        </DyDataStateContext.Provider>
    );
};
